<script setup>
import { ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const selectedProvider = ref('');
const useChannel = ref(false);
const isLoading = ref(false);
const channelInfo = ref(null);
const sharepointSiteUrl = ref('');
const sharedDriveId = ref('');

const providers = [
  { value: 'google', label: 'Google Drive', icon: 'i-woot-google-drive' },
  // {
  //   value: 'google_shared_drive',
  //   label: 'Google Shared Drive',
  //   icon: 'i-woot-google-drive',
  // },
  {
    value: 'onedrive',
    label: 'Microsoft OneDrive',
    icon: 'i-woot-sharepoint',
  },
  {
    value: 'sharepoint',
    label: 'Microsoft SharePoint',
    icon: 'i-woot-sharepoint',
  },
  { value: 'dropbox', label: 'Dropbox', icon: 'i-woot-dropbox' },
];

const checkChannelAvailability = async provider => {
  if (
    provider === 'dropbox' ||
    provider === 'sharepoint' ||
    provider === 'google_shared_drive'
  ) {
    channelInfo.value = null;
    return;
  }

  try {
    const action =
      provider === 'google'
        ? 'drives/checkGoogleChannel'
        : 'drives/checkMicrosoftChannel';
    const response = await store.dispatch(action);
    channelInfo.value = response;
  } catch (error) {
    channelInfo.value = null;
  }
};

const handleProviderSelect = async provider => {
  selectedProvider.value = provider;
  useChannel.value = false;
  sharepointSiteUrl.value = '';
  sharedDriveId.value = '';
  await checkChannelAvailability(provider);
};

const handleConnect = async () => {
  if (!selectedProvider.value) {
    useAlert(t('INTEGRATION_SETTINGS.DRIVES.ADD_MODAL.SELECT_PROVIDER_ERROR'));
    return;
  }

  if (selectedProvider.value === 'sharepoint' && !sharepointSiteUrl.value) {
    useAlert('Please enter your SharePoint domain URL');
    return;
  }

  if (
    selectedProvider.value === 'google_shared_drive' &&
    !sharedDriveId.value
  ) {
    useAlert('Please enter your Shared Drive ID');
    return;
  }

  isLoading.value = true;

  try {
    const providerName =
      selectedProvider.value === 'google_shared_drive'
        ? 'googleSharedDrive'
        : selectedProvider.value;
    const action = `drives/authorize${providerName.charAt(0).toUpperCase() + providerName.slice(1)}`;

    let response;
    if (selectedProvider.value === 'sharepoint') {
      response = await store.dispatch(action, sharepointSiteUrl.value);
    } else if (selectedProvider.value === 'google_shared_drive') {
      response = await store.dispatch(action, sharedDriveId.value);
    } else if (selectedProvider.value === 'dropbox') {
      response = await store.dispatch(action);
    } else {
      response = await store.dispatch(action, useChannel.value);
    }

    if (response.authorization_url) {
      window.location.href = response.authorization_url;
    }
  } catch (error) {
    useAlert(
      error?.message || t('INTEGRATION_SETTINGS.DRIVES.ADD_MODAL.ERROR')
    );
    isLoading.value = false;
  }
};

const handleClose = () => {
  emit('close');
};
</script>

<template>
  <div
    class="w-[25rem] z-50 min-w-0 absolute top-10 ltr:right-0 rtl:left-0 bg-n-alpha-3 backdrop-blur-[100px] p-6 rounded-xl border border-slate-50 dark:border-slate-900 shadow-md flex flex-col gap-6 max-h-[85vh] overflow-y-auto"
  >
    <h3 class="text-base font-medium text-slate-900 dark:text-slate-50">
      {{ $t('INTEGRATION_SETTINGS.DRIVES.ADD_MODAL.TITLE') }}
    </h3>

    <p class="text-sm text-slate-600 dark:text-slate-400">
      {{ $t('INTEGRATION_SETTINGS.DRIVES.ADD_MODAL.DESCRIPTION') }}
    </p>

    <div class="space-y-3">
      <label class="text-sm font-medium text-slate-700 dark:text-slate-300">
        {{ $t('INTEGRATION_SETTINGS.DRIVES.ADD_MODAL.SELECT_PROVIDER') }}
      </label>
      <div
        v-for="provider in providers"
        :key="provider.value"
        class="flex items-center p-4 border rounded-lg cursor-pointer transition-colors"
        :class="[
          selectedProvider === provider.value
            ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/20'
            : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600',
        ]"
        @click="handleProviderSelect(provider.value)"
      >
        <input
          type="radio"
          :value="provider.value"
          :checked="selectedProvider === provider.value"
          class="mr-3"
        />
        <Icon
          :icon="provider.icon"
          class="w-6 h-6 mr-3 text-slate-700 dark:text-slate-300"
        />
        <span class="text-base font-medium text-slate-800 dark:text-slate-100">
          {{ provider.label }}
        </span>
      </div>
    </div>

    <div v-if="selectedProvider === 'sharepoint'" class="space-y-2">
      <label class="text-sm font-medium text-slate-700 dark:text-slate-300">
        SharePoint Domain
      </label>
      <input
        v-model="sharepointSiteUrl"
        type="url"
        placeholder="https://yourcompany.sharepoint.com"
        class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
      />
      <p class="text-xs text-slate-500 dark:text-slate-400">
        Enter your SharePoint domain URL (e.g.,
        https://yourcompany.sharepoint.com or
        https://yourcompany-my.sharepoint.com)
      </p>
    </div>

    <div v-if="selectedProvider === 'google_shared_drive'" class="space-y-2">
      <label class="text-sm font-medium text-slate-700 dark:text-slate-300">
        Shared Drive URL
      </label>
      <input
        v-model="sharedDriveId"
        type="url"
        placeholder="https://drive.google.com/drive/folders/0AOg6SeJXvP4YUk9PVA"
        class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
      />
      <p class="text-xs text-slate-500 dark:text-slate-400">
        Enter the URL of the Shared Drive. Copy it from your browser when
        viewing the Shared Drive in Google Drive.
      </p>
    </div>

    <div
      v-if="channelInfo && selectedProvider !== 'dropbox'"
      class="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
    >
      <label class="flex items-start cursor-pointer">
        <input v-model="useChannel" type="checkbox" class="mt-1 mr-3" />
        <div>
          <p class="text-sm font-medium text-slate-800 dark:text-slate-100">
            {{ $t('INTEGRATION_SETTINGS.DRIVES.ADD_MODAL.USE_CHANNEL') }}
          </p>
          <p class="text-xs text-slate-600 dark:text-slate-400 mt-1">
            {{
              $t('INTEGRATION_SETTINGS.DRIVES.ADD_MODAL.CHANNEL_INFO', {
                email: channelInfo.email,
              })
            }}
          </p>
        </div>
      </label>
    </div>

    <div class="flex gap-2 justify-end">
      <Button
        size="sm"
        variant="ghost"
        :disabled="isLoading"
        @click="handleClose"
      >
        {{ $t('INTEGRATION_SETTINGS.DRIVES.ADD_MODAL.CANCEL') }}
      </Button>
      <Button
        size="sm"
        variant="solid"
        color="blue"
        :disabled="!selectedProvider || isLoading"
        @click="handleConnect"
      >
        {{
          isLoading
            ? $t('INTEGRATION_SETTINGS.DRIVES.ADD_MODAL.CONNECTING')
            : $t('INTEGRATION_SETTINGS.DRIVES.ADD_MODAL.CONNECT')
        }}
      </Button>
    </div>
  </div>
</template>
